"use client";

import { useMemo, useState } from "react";
import { ChevronDown, ChevronsUpDown, ChevronUp } from "lucide-react";
import {
  Card,
  CardContent,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@repo/ui";

/**
 * Une colonne de score du récapitulatif. Une colonne = un test de la campagne,
 * et il n'y a QU'UNE valeur par test : le score global. Le détail par dimension
 * vit dans les onglets Statistiques et Matching, pas ici — ce tableau sert à
 * balayer une campagne d'un coup d'œil.
 */
export type ColonneSynthese = {
  /** Clé de lecture dans `LigneSynthese.scores`. */
  key: string;
  label: string;
  /** Ce que le score mesure, en infobulle : les colonnes n'ont pas toutes la
   *  même nature (matching à un référentiel vs couverture d'un barème). */
  aide?: string;
  /**
   * Texte et teinte de la pastille. Par défaut un pourcentage en paliers. Le PC
   * Fit s'en sert pour r, dont la lecture dépend aussi du p — absent de
   * `scores`, qui ne porte que la valeur triée.
   */
  pastille?: (
    ligne: LigneSynthese,
    score: number,
  ) => { texte: string; classes: string };
};

/** Un candidat de la campagne, avec son score par test. `null` = pas de score
 *  exploitable (test non passé, référentiel absent, barème vide). */
export type LigneSynthese = {
  parcoursId: string;
  prenomCandidat?: string;
  nomCandidat?: string;
  scores: Record<string, number | null>;
};

type Props = {
  lignes: LigneSynthese[];
  /** Colonnes à afficher, dans l'ordre. Un test non configuré sur la campagne
   *  n'a PAS de colonne — une colonne entièrement vide ne dit rien. */
  colonnes: ColonneSynthese[];
};

type Tri = { colonne: string; direction: "asc" | "desc" };

/**
 * Palier du score, en une seule teinte : ces pourcentages n'ont pas de pôle
 * « mauvais » à colorer en rouge — un matching bas dit une distance au
 * référentiel, pas un échec du candidat.
 *
 * Trois paliers plutôt qu'un dégradé continu : le niveau se lit d'un coup
 * d'œil, et chaque couple fond/texte tient le contraste AA (≥ 4,5:1). L'ancien
 * dégradé laissait le texte foncé sur un prune à 80 % (~3,4:1) — les meilleurs
 * scores étaient les moins lisibles. Fond mélangé au blanc, pas au transparent :
 * le survol de ligne ne doit pas changer la teinte de la pastille.
 */
export const TEINTES_PASTILLE = {
  haute: "bg-primary text-primary-foreground",
  moyenne: "bg-[color-mix(in_srgb,var(--primary)_35%,white)] text-foreground",
  basse: "bg-muted text-foreground",
} as const;

function pastilleParDefaut(score: number): { texte: string; classes: string } {
  // Sur la valeur affichée : un 69,6 lu « 70 % » doit avoir la pastille de 70.
  const arrondi = Math.round(score);
  return {
    texte: `${arrondi} %`,
    classes:
      arrondi >= 70
        ? TEINTES_PASTILLE.haute
        : arrondi >= 40
          ? TEINTES_PASTILLE.moyenne
          : TEINTES_PASTILLE.basse,
  };
}

function nomComplet(ligne: LigneSynthese): string {
  return `${ligne.prenomCandidat ?? ""} ${ligne.nomCandidat ?? ""}`.trim();
}

/**
 * Récapitulatif d'une campagne : une ligne par candidat, une colonne par test,
 * et pour valeur le seul score global de ce test.
 *
 * Rendu PUR — les scores arrivent déjà calculés (`SyntheseTab` s'en charge, il a
 * seul accès aux référentiels figés de la campagne).
 *
 * Pas de colonne agrégée toutes-tests confondus : les échelles ne sont pas
 * commensurables (un matching à des fourchettes de référents et une couverture
 * de barème ne se moyennent pas), donc la comparaison se fait colonne par
 * colonne, par le tri.
 */
export function SyntheseCampagneTable({ lignes, colonnes }: Props) {
  // Tri initial : première colonne de score, décroissant — le haut du
  // classement du premier test est ce qu'on vient lire.
  const [tri, setTri] = useState<Tri>(() => ({
    colonne: colonnes[0]?.key ?? "candidat",
    direction: "desc",
  }));

  const lignesTriees = useMemo(() => {
    const sens = tri.direction === "desc" ? -1 : 1;
    if (tri.colonne === "candidat") {
      return [...lignes].sort(
        (a, b) =>
          sens *
          nomComplet(a).localeCompare(nomComplet(b), "fr", {
            sensitivity: "base",
          }),
      );
    }
    // Les candidats sans score finissent toujours la liste, quel que soit le
    // sens du tri : `null` n'est pas un score bas, c'est une absence de mesure,
    // et le faire remonter en tri croissant tromperait la lecture.
    const avec = lignes.filter((l) => l.scores[tri.colonne] != null);
    const sans = lignes.filter((l) => l.scores[tri.colonne] == null);
    avec.sort(
      (a, b) =>
        sens * ((a.scores[tri.colonne] ?? 0) - (b.scores[tri.colonne] ?? 0)),
    );
    return [...avec, ...sans];
  }, [lignes, tri]);

  const basculerTri = (colonne: string) => {
    setTri((prev) =>
      prev.colonne === colonne
        ? { colonne, direction: prev.direction === "desc" ? "asc" : "desc" }
        : // Un score se lit du meilleur au moins bon, un nom de A à Z.
          { colonne, direction: colonne === "candidat" ? "asc" : "desc" },
    );
  };

  // Icône toujours rendue, même hors tri : sa largeur ne doit pas apparaître
  // ni disparaître au clic, sans quoi le titre centré se décale.
  const fleche = (colonne: string) =>
    tri.colonne !== colonne ? (
      <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-40" />
    ) : tri.direction === "desc" ? (
      <ChevronDown className="h-3.5 w-3.5 shrink-0" />
    ) : (
      <ChevronUp className="h-3.5 w-3.5 shrink-0" />
    );

  const ariaSort = (colonne: string) =>
    tri.colonne !== colonne
      ? undefined
      : tri.direction === "desc"
        ? "descending"
        : "ascending";

  if (colonnes.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">
            Aucun test de cette campagne ne produit de score de matching.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (lignes.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">
            Aucun candidat n'a encore de score dans cette campagne.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    // `gap-0 py-0` : le padding par défaut de la carte laisse un bandeau vide
    // au-dessus des titres. `overflow-clip` recadre le fond de l'en-tête aux
    // angles arrondis (même réglage que `DataTable`).
    // Liens des cellules en contour de focus intérieur : cf. `DataTable`.
    <Card className="gap-0 py-0 overflow-clip [&_td_a:focus-visible]:-outline-offset-2">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          {/* `table-fixed` : sans lui, le navigateur répartit la largeur au
              prorata du contenu et un libellé long écrase ses voisins. Le
              plancher de largeur fait défiler plutôt que tasser sur mobile. */}
          <table
            className="w-full table-fixed text-sm"
            style={{ minWidth: `${16 + colonnes.length * 9}rem` }}
          >
            {/* Candidat à largeur fixe, les scores se partagent le reste à
                parts égales. */}
            <colgroup>
              <col style={{ width: "16rem" }} />
              {colonnes.map((col) => (
                <col key={col.key} />
              ))}
            </colgroup>
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th
                  scope="col"
                  aria-sort={ariaSort("candidat")}
                  className="px-4 py-3 text-left font-medium text-muted-foreground whitespace-nowrap"
                >
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                    onClick={() => basculerTri("candidat")}
                  >
                    Candidat
                    {fleche("candidat")}
                  </button>
                </th>
                {colonnes.map((col) => (
                  <th
                    key={col.key}
                    scope="col"
                    aria-sort={ariaSort(col.key)}
                    className="px-3 py-3 text-center font-medium text-muted-foreground"
                  >
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                      onClick={() => basculerTri(col.key)}
                    >
                      {/* Contrepoids de l'icône de tri : garde le libellé
                          centré sur la pastille en dessous. */}
                      <span aria-hidden className="w-3.5 shrink-0" />
                      {col.aide ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>{col.label}</span>
                          </TooltipTrigger>
                          {/* Aide sur plusieurs lignes : l'arrondi pilule
                              par défaut rognerait les coins du texte. `!` car
                              tailwind-merge ne reconnaît pas les rayons
                              maison et garderait les deux classes. */}
                          <TooltipContent className="max-w-xs rounded-surface! px-4 py-3 leading-relaxed">
                            {col.aide}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        col.label
                      )}
                      {fleche(col.key)}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lignesTriees.map((ligne) => (
                <tr
                  key={ligne.parcoursId}
                  className="border-t border-border hover:bg-muted/20 transition-colors"
                >
                  <td
                    className="px-4 py-3 font-medium truncate"
                    title={nomComplet(ligne) || undefined}
                  >
                    {nomComplet(ligne) || (
                      <span className="text-muted-foreground italic">
                        Anonyme
                      </span>
                    )}
                  </td>
                  {colonnes.map((col) => {
                    const score = ligne.scores[col.key];
                    const pastille =
                      score != null
                        ? (col.pastille ?? ((_, s) => pastilleParDefaut(s)))(
                            ligne,
                            score,
                          )
                        : null;
                    return (
                      <td key={col.key} className="px-3 py-3 text-center">
                        {pastille ? (
                          <span
                            className={`inline-block min-w-14 rounded-pastille px-2 py-0.5 text-center text-xs font-semibold tabular-nums ${pastille.classes}`}
                          >
                            {pastille.texte}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
